import React, { useRef, useState } from 'react';
import { BetslipContext } from '../betslip/BetslipConf';
import { getConfiguration, getCurrencySymbol, getLocaleDotRound, getPrintableAmount, getValidAmountString, getAmountInCent, getReservationCodeType, setReservationCodeType } from '../utils';
import { getLabel } from '../../InitData';
import Keypad from './Keypad';
import { xs_isMobile } from '../..';

function getTogglesToShow(conf) {
    const togglesToShow = new Set();
    Object.values(conf.vls).forEach(v => {
        if (v.sh === true) {
            togglesToShow.add(v.ty);
        }
    });
    return togglesToShow;
}

function Settings(props) {
    const conf = getConfiguration();
    const betslipContext = React.useContext(BetslipContext);
    
    const nDec = conf.cs.nd;
    let betslipData = betslipContext.getBetslipData();
    const couponKey = betslipData.selectedCoupon;
    const coupon = betslipData.coupons[couponKey];
    let varOdd = coupon.oddVariation;
    const defaultAmount = getPrintableAmount(betslipData.defaultAmount, conf.cs.m, conf.cs.nd);
    const defaultMinAmount = getPrintableAmount(betslipContext.minimumBetAmount, conf.cs.m, conf.cs.nd);
    const unitMultAmount = getPrintableAmount(betslipContext.minimumUnitAmount, conf.cs.m, conf.cs.nd);
    const currency = getCurrencySymbol(conf.cs.ct);
	const charDecimalBrowser = getLocaleDotRound().round;

    const [showKeypad, setShowKeypad] = useState(false);
    const [showError, setShowError] = useState(false);
    const [togglesToShow] = useState(getTogglesToShow(conf.ovs));
    const [useQrCode, setUseQrCode] = useState(getReservationCodeType() === 'qrCode');
    const [inputValue, setInputValue] = useState(defaultMinAmount);

    function changeVarOdd(oddVariation) {
		betslipContext.changeOddVariation(oddVariation, true);
    }
    
    function toggleSaveBetslip() {
    	betslipContext.toggleSaveBetslipAfterPurchase();    
    }

    function toggleKeypad() {
        setShowKeypad(!showKeypad);
    }

    function closeKeypad() {
        setShowKeypad(false);
    }

    function toggleReservationPrintType() {
        if (useQrCode) {
            setReservationCodeType('barcode');
        } else {
            setReservationCodeType('qrCode');
        }
        setUseQrCode(!useQrCode);
    }

    function setDefaultAmount(amount) {
        // get a valid input field value for this amount: only number and dot, and max nDec digit after it
        let parsedAmount = getValidAmountString(amount, nDec);
        setInputValue(parsedAmount.replace('.', charDecimalBrowser));
        // if is number, I calculate the new amount and I keep it into the field value. 
        // If not, I keep the field but I use the last valid amount for the calcs
        if (!isNaN(parsedAmount) && parsedAmount.length > 0) {
            const mult = conf.cs.m;
            parsedAmount = getAmountInCent(parseFloat(parsedAmount), mult);
        }

        const amountUnit = betslipContext.minimumUnitAmount;
		const isMultiple = (parsedAmount % amountUnit) === 0;
		if (parsedAmount < betslipContext.minimumBetAmount || isMultiple === false ) {
			setShowError(true);
		} else {
            betslipContext.setDefaultAmount(parsedAmount);
            setShowError(false);
        }
    }

    return (
        <>
            <div className="customInput customInput--toggle">
                <label>
                    <input type="checkbox" checked={useQrCode} onChange={toggleReservationPrintType} />
                    <span></span>
                    <div className="customInput__description">{getLabel('betslip_alt.use_qr_code_for_reserved_ticked')}</div>
                </label>
            </div>
            {togglesToShow.size > 0 &&
                <span>{getLabel('betslip_alt.accept_odds_variation')}</span>
            }
            {togglesToShow.has('NEVER') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'NEVER'} onChange={() => changeVarOdd('NEVER')} />
                        <span></span>
                        <div className="customInput__description">{getLabel('betslip_alt.accept_odds_variation.never')}</div>
                    </label>
                </div>
            }
            {togglesToShow.has('ALWAYS') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'ALWAYS'} onChange={() => changeVarOdd('ALWAYS')} />
                        <span></span>
                        <div className="customInput__description">{getLabel('betslip_alt.accept_odds_variation.always')}</div>
                    </label>
                </div>
            }
            {togglesToShow.has('INCR') &&
                <div className="customInput customInput--radio">
                    <label>
                        <input type="radio" name="radio" checked={varOdd === 'INCR'} onChange={() => changeVarOdd('INCR')} />
                        <span></span>
                        <div className="customInput__description">{getLabel('betslip_alt.accept_odds_variation.increasing')}</div>
                    </label>
                </div>
            }
            <div className="customInput customInput--toggle">
                <label>
                    <input type="checkbox" checked={betslipContext.isSaveBetslipAfterPurchaseEnabled()} onChange={toggleSaveBetslip} />
                    <span></span>
                    <div className="customInput__description">{getLabel('betslip_alt.save_ticket')}</div>
                </label>
            </div>
            <div className="cart__footer__betInputRow">
                <span>{getLabel('betslip_alt.default_amount')}</span>
                {xs_isMobile ?
                    <div className="calculatorInput" onClick={toggleKeypad}>{defaultAmount} {currency}</div>
                    :
                    <div className="calculatorInput__container">
                        <input className="calculatorInput"
                	            onChange={(e) => setDefaultAmount(`${e.target.value}`)}
                                value={inputValue} />
                        <div className="calculatorInput__currency">{currency}</div>
                    </div>
                }
                {showKeypad &&
                    <Keypad amount={defaultAmount}
                            currency={currency}
                            decimalsEnabled={true}
                            decimalSeparator={charDecimalBrowser}
                            setAmount={setDefaultAmount}
                            close={closeKeypad} />
                }
            </div>
            {showError &&
                <div className="text-small">{getLabel('betslip_alt.error.minimum_amount_part1')} {defaultMinAmount} {currency} {getLabel('betslip_alt.error.minimum_amount_part2')} {unitMultAmount} {currency}</div>
            }
        </>
    );
}

export default Settings;
